import _ from "lodash";
import "./MissionInfoForm.scss";
import InfoContext from "pages/InfoContext";
import { FormattedMessage } from "react-intl";
import {
  MISSION_TYPE,
  LANG,
  TAG_TYPES,
  MINWORDMISSIONDESCRIPTION,
} from "constants/constants";
import { Typography } from "core/typography/Typography";
import {
  switchDataLeft,
  switchDataRight,
} from "../../../constants/MissionSwitchData";
import { tabletSize } from "utils/screenSizes";
import { useContext, useEffect } from "react";
import {
  FORM_INFO,
  MISSION_FORM_LABELS,
  MISSION_FORM_PLACEHOLDERS,
  JOB_CATEGORY_LABEL,
} from "constants/forms";
import {
  renderInputMissionName,
  renderInputCsltJob,
  renderInputJobCategory,
  renderInputMissionType,
  renderAppealSelect,
  renderInputBranch,
  renderInputManager,
  renderInputCsltStr,
  renderInputMissionDates,
  renderInputMissionDescription,
  renderInputSwitch,
} from "../CommonInputs";
import { countWords, isMissionDescriptionTooSmall } from "utils/missionHelper";
import PropTypes from "prop-types";
import { MISSION_MANAGER_ROLES } from "access-roles/roles";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { getCellErrorHelper } from "utils/helper";
import { useJobCategoryCascade } from "utils/customHooks";

const MissionInfoForm = ({
  control,
  errors,
  register,
  setValue,
  getValues,
  watch,
  unregister,
  clearErrors,
}) => {
  const {
    managerBranches,
    davFunctions,
    getOptions,
    personsByProfileList,
    missionById,
    appealList,
    readOnly,
    lang,
    cellHighlight,
    missingCharValue,
    setMissingCharValue,
    setErrorNameMessage,
    errorNameMessage,
    setErrorWordDescriptionMessage,
    errorWordDescriptionMessage,
    setDescriptionMessageWordValue,
    descriptionMessageWordValue,
    setDescriptionMessageDashValue,
    descriptionMessageDashValue,
    setErrorDashDescriptionMessage,
    errorDashDescriptionMessage,
    apiErrorResponse,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { domain_category_id, domain_id, jobCategoryList, jobOptions } =
    useJobCategoryCascade({
      watch,
      setValue,
      clearErrors,
      jobs: davFunctions,
    });

  const manager_id = watch("manager_id", []);
  const worker_id = watch("worker_id", []);
  const signed_by_id = watch("signed_by_id", []);
  const missionName = watch("name", "");
  const description = watch("description", "");
  const type = watch("type_");
  const is_visible = watch("is_visible", null);
  const appeal_id = watch("appeal_id");

  useEffect(() => {
    return typeof missionById?.is_confidential === "boolean"
      ? setValue("is_visible", !missionById?.is_confidential)
      : setValue("is_visible", null);
  }, [missionById?.is_confidential]);

  useEffect(() => {
    const fullDescription = getValues("description");
    const wordCount = countWords(fullDescription);
    if (isMissionDescriptionTooSmall(fullDescription)) {
      setErrorWordDescriptionMessage("mission_description_word_count");
      setDescriptionMessageWordValue(MINWORDMISSIONDESCRIPTION - wordCount);
    } else {
      setDescriptionMessageWordValue(0);
    }

    const dashCount = fullDescription
      ? (fullDescription.match(/[-•◦▪▫▸➔★✔✗♦◊❏❖➢]/g) || []).length
      : 0;

    if (dashCount < 5) {
      setErrorDashDescriptionMessage("mission_description_dash_count");
      setDescriptionMessageDashValue(5 - dashCount);
    }
  }, [description]);

  useEffect(() => {
    if (worker_id[0]?.profile !== "str") {
      setValue("appeal_id", null);
      unregister("appeal_id");
    }
  }, [worker_id]);

  const inputsDate = [
    {
      label: MISSION_FORM_LABELS.mission_start_date,
      placeholder: MISSION_FORM_LABELS.mission_start_date,
      required: true,
      name: "start_date",
      id: FORM_INFO.mission_start_date.id,
      max: watch("end_date"),
      readOnly: readOnly,
      errorMessage: apiErrorResponse?.["start_date"] || null,
    },
    {
      label: MISSION_FORM_LABELS.mission_end_date,
      placeholder: MISSION_FORM_LABELS.mission_end_date,
      required: false,
      name: "end_date",
      id: FORM_INFO.mission_end_date.id,
      min: watch("start_date"),
      readOnly: readOnly,
      errorMessage: apiErrorResponse?.["end_date"] || "mission_end_date_error",
    },
  ];

  const renderInputDatesMission = () =>
    inputsDate.map((input) => (
      <div
        className={`cell-padding ${getCellErrorHelper({
          hasValue: input.required ? watch(input.name) : true,
          hasError: errors?.[input.name] || apiErrorResponse?.[input.name],
          cellHighlight,
        })}`}
        key={input.id}
      >
        {renderInputMissionDates({
          clearErrors,
          errorMessage: input.errorMessage,
          errorValue: input.errorValue,
          control,
          errors,
          label: input.label,
          placeholder: input.placeholder,
          readOnly: input.readOnly,
          register,
          lang,
          name: input.name,
          id: input.id,
          required: input.required,
          min: input?.min,
          max: input?.max,
          setValue,
          value: watch(input.name),
        })}
      </div>
    ));

  const buildToggleSwitchData = (data) =>
    Object.entries(data).map(([name, value]) => {
      const fieldValue =
        value.name === "is_visible" ? is_visible : watch(value.name);
      const hasError = fieldValue === null && cellHighlight;
      return (
        <div
          key={name}
          className={hasError ? "errorCellColor" : ""}
          style={hasError ? { borderRadius: "8px", padding: "4px" } : {}}
        >
          {renderInputSwitch({
            clearErrors,
            control,
            errors,
            readOnly,
            register,
            setValue,
            value: fieldValue,
            name: value.name,
            data: value.switchData,
            topUp: value.switchData,
          })}
        </div>
      );
    });

  return (
    <div className="mission-infos-wrapper" style={{ paddingTop: "4rem" }}>
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={<FormattedMessage id="mission_infos" defaultMessage="MISSION" />}
      />
      <div className="section-1">
        <div className="line-1">
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: missionName,
              hasError: errors?.["name"] || apiErrorResponse?.["name"],
              cellHighlight,
            })}`}
          >
            {renderInputMissionName({
              control,
              errors,
              label: MISSION_FORM_LABELS.mission_name,
              placeholder: MISSION_FORM_LABELS.mission_name,
              register,
              value: missionName,
              setValue,
              readOnly,
              errorValue: missingCharValue === 0 ? "" : missingCharValue,
              errorMessage: apiErrorResponse?.["name"] || errorNameMessage,
              setMissingCharValue,
              setErrorNameMessage,
            })}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: type,
              hasError: errors?.["type_"] || apiErrorResponse?.["type_"],
              cellHighlight,
            })}`}
          >
            {renderInputMissionType({
              control,
              errors,
              label: MISSION_FORM_LABELS.mission_type,
              placeholder: MISSION_FORM_LABELS.mission_type,
              readOnly,
              register,
              value: type,
              setValue,
              options: MISSION_TYPE,
              errorValue: null,
              errorMessage:
                apiErrorResponse?.["type_"] ||
                errors?.["type_"]?.message ||
                null,
              clearErrors,
            })}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: domain_category_id,
              hasError:
                errors?.["domain_category_id"] ||
                apiErrorResponse?.["domain_category_id"],
              cellHighlight,
            })}`}
          >
            {renderInputJobCategory({
              control,
              jobCategories: jobCategoryList,
              errors,
              label: JOB_CATEGORY_LABEL,
              readOnly,
              register,
              setValue,
              lang,
              errorMessage:
                apiErrorResponse?.["domain_category_id"] ||
                errors?.["domain_category_id"]?.message ||
                null,
              clearErrors,
            })}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: domain_id.length,
              hasError:
                errors?.["domain_id"] || apiErrorResponse?.["domain_id"],
              cellHighlight,
            })}`}
          >
            {renderInputCsltJob({
              control,
              davFunctions: jobOptions,
              errors,
              label: MISSION_FORM_LABELS.cslt_job_mission,
              placeholder: MISSION_FORM_LABELS.cslt_job_mission,
              readOnly,
              // The category comes first: it is what narrows this list
              // (redmine 12939).
              lockedUntilCategory: !domain_category_id,
              register,
              setValue,
              lang,
              value: domain_id,
              errorValue: null,
              errorMessage:
                apiErrorResponse?.["domain_id"] ||
                errors?.["domain_id"]?.message ||
                null,
              clearErrors,
            })}
          </div>
        </div>
        <div className="line-2">
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: manager_id,
              hasError:
                errors?.["manager_id"] || apiErrorResponse?.["manager_id"],
              cellHighlight,
            })}`}
          >
            {renderInputManager({
              control,
              register,
              name: "manager_id",
              personsByProfileList,
              readOnly: !_.isNil(missionById?.id),
              getOptions,
              errors:
                errors?.["manager_id"] || apiErrorResponse?.["manager_id"],
              label: MISSION_FORM_LABELS.mission_manager_id,
              placeholder: MISSION_FORM_LABELS.mission_manager_id,
              setValue,
              selectedOptions: manager_id,
              value: manager_id,
              errorValue: null,
              errorMessage:
                apiErrorResponse?.["manager_id"] ||
                errors?.["manager_id"]?.message,
              getNamesByFilters: getNamesByFilters,
              namesByFilters: namesByFilters,
              role: MISSION_MANAGER_ROLES,
            })}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: signed_by_id,
              hasError:
                errors?.["signed_by_id"] || apiErrorResponse?.["signed_by_id"],
              cellHighlight,
            })}`}
          >
            {renderInputBranch({
              control,
              errors,
              label: MISSION_FORM_LABELS.signed_by_id,
              placeholder: MISSION_FORM_LABELS.signed_by_id,
              required: true,
              name: "signed_by_id",
              value: signed_by_id,
              readOnly,
              register,
              setValue,
              branches: managerBranches,
              type: TAG_TYPES.BRANCHES,
            })}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: worker_id.length,
              hasError:
                errors?.["worker_id"] || apiErrorResponse?.["worker_id"],
              cellHighlight,
            })}`}
          >
            {renderInputCsltStr({
              control,
              register,
              readOnly,
              errors,
              getNamesByFilters: getNamesByFilters,
              namesByFilters: namesByFilters,
              label: MISSION_FORM_LABELS.worker_id,
              placeholder: MISSION_FORM_LABELS.worker_id,
              setValue,
              value: worker_id,
              profile: ["cslt", "str"],
              clearErrors,
              errorMessage:
                apiErrorResponse?.["worker_id"] ||
                errors?.["worker_id"]?.message,
            })}
          </div>
          {worker_id[0]?.profile === "str" && tabletSize && (
            <div
              className={`cell-padding ${getCellErrorHelper({
                hasValue: appeal_id,
                cellHighlight,
              })}`}
            >
              {renderAppealSelect({
                control,
                errors,
                label: MISSION_FORM_LABELS.appeal_id,
                placeholder: MISSION_FORM_LABELS.appeal_id,
                readOnly,
                register,
                setValue,
                options: appealList?.map((elm) => ({
                  label: lang === LANG.FR ? elm.fr : elm.en,
                  value: elm.id,
                })),
                clearErrors,
              })}
            </div>
          )}
          {renderInputDatesMission()}
        </div>
        <div className="line-3">
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: description,
              hasError:
                errors?.["description"] || apiErrorResponse?.["description"],
              cellHighlight,
            })}`}
          >
            {renderInputMissionDescription({
              control,
              errors,
              label: MISSION_FORM_LABELS.description,
              placeholder: MISSION_FORM_PLACEHOLDERS.description,
              register,
              value: description,
              setValue,
              isMission: false,
              readOnly,
              errorValue: descriptionMessageWordValue,
              errorMessage:
                apiErrorResponse?.["description"] ||
                errorWordDescriptionMessage,
              additionalErrorValue: descriptionMessageDashValue,
              additionalErrorMessage: errorDashDescriptionMessage,
            })}
          </div>
          {worker_id[0]?.profile === "str" && !tabletSize && (
            <div
              className={`cell-padding ${getCellErrorHelper({
                hasValue: appeal_id,
                cellHighlight,
              })}`}
            >
              {renderAppealSelect({
                control,
                errors,
                label: MISSION_FORM_LABELS.appeal_id,
                placeholder: MISSION_FORM_LABELS.appeal_id,
                readOnly,
                register,
                setValue,
                options: appealList?.map((elm) => ({
                  label: lang === LANG.FR ? elm.fr : elm.en,
                  value: elm.id,
                })),
                clearErrors,
              })}
            </div>
          )}
          <div className="switchParent">
            <div className="switchToggle">
              <div className="left">
                {buildToggleSwitchData(switchDataLeft)}
              </div>
              <div className="right">
                {buildToggleSwitchData(switchDataRight)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionInfoForm;

MissionInfoForm.propTypes = {
  control: PropTypes.object,
  errors: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
  unregister: PropTypes.func,
  getValues: PropTypes.func,
};
